import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import { format } from 'date-fns';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import {
  showCancelAlert,
  showCancelConfirmation,
  showError,
} from '../../../../core/alert/sweetAlertConfig'; // Adjust the import path

interface RecurringBooking {
  id: number;
  vehicleBookingRequestId: number;
  reason: string;
  noOfPassengers: number;
  departureTime: number;
  frequency: string;
  endDate: number;
  status: string;
  createdBy: string;
  createdAt: number;
}

interface RecurringBookingsModalProps {
  show: boolean;
  onHide: () => void;
  recurringDetails: RecurringBooking[];
  recurringModalError: string | null;
  statusColors: { [key: string]: string };
  setRecurringDetails: (details: RecurringBooking[]) => void;
}

interface ApiResponse<T> {
  status: string;
  statusDescription: string;
  data: T;
}

interface RecurringVehicleBookingResponse {
  bookings: RecurringBooking[];
  total?: number;
}

interface SingleRecurringBookingResponse {
  bookings: RecurringBooking[];
}

const RecurringBookingsModal: React.FC<RecurringBookingsModalProps> = ({
  show,
  onHide,
  recurringDetails,
  recurringModalError,
  statusColors,
  setRecurringDetails,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<RecurringBooking | null>(null);
  const [editFormData, setEditFormData] = useState<{
    frequency: string;
    endDate: string;
    departureTime: string;
  }>({
    frequency: '',
    endDate: '',
    departureTime: '',
  });

  const pageSize = 7;

  const fetchRecurringBookings = async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiDefinitions.getRecurringBookings(page);
      const responseData = response.data as ApiResponse<RecurringVehicleBookingResponse>;
      if (responseData.status === 'S1000' && responseData.data?.bookings) {
        setRecurringDetails(responseData.data.bookings);
        setTotalRecords(responseData.data.total || responseData.data.bookings.length);
        setError(null);
      } else {
        setRecurringDetails([]);
        setError(responseData.statusDescription || 'No recurring bookings available.');
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.statusDescription ||
        err.message ||
        'Failed to fetch recurring bookings.';
      showError(errorMessage); // Use SweetAlert for error
      setRecurringDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async (id: number) => {
    if (!checkPermission('ROLE_CANCEL_RECURRING_VEHICLE_BOOKING_REQUESTS')) {
      showError('You do not have permission to cancel recurring bookings.');
      return;
    }
    showCancelConfirmation(async () => {
      try {
        const response = await apiDefinitions.cancelRecurringBooking(id);
        if (response.data.status === 'S1000') {
          showCancelAlert(); // Show success alert
          fetchRecurringBookings(currentPage);
        } else {
          showError(response.data.statusDescription || 'Failed to cancel recurring booking.');
        }
      } catch (err: any) {
        showError(
          err.response?.data?.statusDescription || 'Failed to cancel recurring booking.'
        );
      }
    });
  };

  const handleView = async (id: number) => {
    if (!checkPermission('ROLE_GET_RECURRING_VEHICLE_BOOKING_REQUESTS')) {
      showError('You do not have permission to view recurring bookings.');
      return;
    }
    try {
      const response = await apiDefinitions.getRecurringBookingById(id);
      const responseData = response.data as ApiResponse<SingleRecurringBookingResponse>;
      if (responseData.status === 'S1000' && responseData.data?.bookings?.length > 0) {
        const booking = responseData.data.bookings[0];
        setSelectedBooking(booking);
        setShowViewModal(true);
      } else {
        showError(
          responseData.statusDescription ||
            'No booking details found for the specified ID.'
        );
      }
    } catch (err: any) {
      showError(
        err.response?.data?.statusDescription ||
          err.message ||
          'Failed to fetch booking details.'
      );
    }
  };

  const handleEdit = async (id: number) => {
    if (!checkPermission('ROLE_UPDATE_RECURRING_VEHICLE_BOOKING_REQUESTS')) {
      showError('You do not have permission to update recurring bookings.');
      return;
    }
    try {
      const response = await apiDefinitions.getRecurringBookingById(id);
      const responseData = response.data as ApiResponse<SingleRecurringBookingResponse>;
      if (responseData.status === 'S1000' && responseData.data?.bookings?.length > 0) {
        const booking = responseData.data.bookings[0];
        setSelectedBooking(booking);
        setEditFormData({
          frequency: booking.frequency || 'DAILY',
          endDate: format(new Date(booking.endDate), 'yyyy-MM-dd'),
          departureTime: format(new Date(booking.departureTime), 'HH:mm'), // Use 24-hour format for consistency
        });
        setShowEditModal(true);
      } else {
        showError(
          responseData.statusDescription ||
            'No booking details found for the specified ID.'
        );
      }
    } catch (err: any) {
      showError(
        err.response?.data?.statusDescription ||
          err.message ||
          'Failed to fetch booking details.'
      );
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedBooking) return;
    try {
      const payload = {
        frequency: editFormData.frequency,
        endDate: new Date(editFormData.endDate).getTime().toString(),
        departureTime: new Date(
          `${editFormData.endDate}T${editFormData.departureTime}:00`
        ).getTime().toString(), // Ensure seconds are included
      };
      const response = await apiDefinitions.updateRecurringBooking(selectedBooking.id, payload);
      if (response.data.status === 'S1000') {
        showCancelAlert(); // Show success alert
        setShowEditModal(false);
        setSelectedBooking(null);
        setEditFormData({ frequency: '', endDate: '', departureTime: '' });
        fetchRecurringBookings(currentPage);
      } else {
        showError(response.data.statusDescription || 'Failed to update recurring booking.');
      }
    } catch (err: any) {
      showError(
        err.response?.data?.statusDescription || 'Failed to update recurring booking.'
      );
    }
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
    setSelectedBooking(null);
    setEditFormData({ frequency: '', endDate: '', departureTime: '' });
  };

  const handleViewModalClose = () => {
    setShowViewModal(false);
    setSelectedBooking(null);
  };

  useEffect(() => {
    if (show && checkPermission('ROLE_GET_RECURRING_VEHICLE_BOOKING_REQUESTS')) {
      fetchRecurringBookings(currentPage);
    } else if (show) {
      showError('You do not have permission to view recurring bookings.');
      setIsLoading(false);
    }
  }, [show, currentPage]);

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Recurring Vehicle Bookings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading && <div className="text-center">Loading...</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {recurringModalError && <div className="alert alert-danger">{recurringModalError}</div>}
          {!isLoading && !error && !recurringModalError && recurringDetails.length === 0 && (
            <div className="alert alert-info">No recurring bookings found.</div>
          )}
          {!isLoading && !error && !recurringModalError && recurringDetails.length > 0 && (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Reason</th>
                    <th>Passengers</th>
                    <th>Departure Time</th>
                    <th>Frequency</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recurringDetails.map((booking) => (
                    <tr key={booking.id}>
                      <td>{booking.vehicleBookingRequestId}</td>
                      <td>{booking.reason}</td>
                      <td>{booking.noOfPassengers}</td>
                      <td>{format(new Date(booking.departureTime), 'PP HH:mm')}</td>
                      <td>{booking.frequency}</td>
                      <td>{format(new Date(booking.endDate), 'PP')}</td>
                      <td>
                        <span className={statusColors[booking.status] || 'badge bg-secondary'}>
                          {booking.status}
                        </span>
                      </td>
                      <td>
                        <div className="hstack gap-2">
                          <Button
                            variant="soft-success"
                            size="sm"
                            className="btn-icon"
                            onClick={() => handleView(booking.id)}
                          >
                            <i className="feather-eye" />
                          </Button>
                          {checkPermission('ROLE_UPDATE_RECURRING_VEHICLE_BOOKING_REQUESTS') && (
                            <Button
                              variant="soft-warning"
                              size="sm"
                              className="btn-icon"
                              onClick={() => handleEdit(booking.id)}
                            >
                              <i className="feather-edit" />
                            </Button>
                          )}
                          {checkPermission('ROLE_CANCEL_RECURRING_VEHICLE_BOOKING_REQUESTS') && (
                            <Button
                              variant="soft-danger"
                              size="sm"
                              className="btn-icon"
                              onClick={() => handleCancel(booking.id)}
                            >
                              <i className="feather-trash-2" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            disabled={currentPage === 1 || isLoading}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          <span>Page {currentPage} of {Math.ceil(totalRecords / pageSize)}</span>
          <Button
            variant="outline-warning"
            disabled={totalRecords <= currentPage * pageSize || isLoading}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      <Modal show={showViewModal} onHide={handleViewModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Recurring Booking Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <div>
              <p><strong>ID:</strong> {selectedBooking.vehicleBookingRequestId}</p>
              <p><strong>Reason:</strong> {selectedBooking.reason}</p>
              <p><strong>Number of Passengers:</strong> {selectedBooking.noOfPassengers}</p>
              <p>
                <strong>Departure Time:</strong>{' '}
                {format(new Date(selectedBooking.departureTime), 'PP HH:mm')}
              </p>
              <p><strong>Frequency:</strong> {selectedBooking.frequency}</p>
              <p>
                <strong>End Date:</strong>{' '}
                {format(new Date(selectedBooking.endDate), 'PP')}
              </p>
              <p><strong>Status:</strong> {selectedBooking.status}</p>
              <p><strong>Created By:</strong> {selectedBooking.createdBy}</p>
              <p>
                <strong>Created At:</strong>{' '}
                {format(new Date(selectedBooking.createdAt), 'PP HH:mm')}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleViewModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={handleEditModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Recurring Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <Form>
              <Form.Group className="mb-3" controlId="formFrequency">
                <Form.Label>Frequency</Form.Label>
                <Form.Select
                  value={editFormData.frequency}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, frequency: e.target.value })
                  }
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3" controlId="formEndDate">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={editFormData.endDate}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, endDate: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="formDepartureTime">
                <Form.Label>Departure Time</Form.Label>
                <Form.Control
                  type="time"
                  value={editFormData.departureTime}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, departureTime: e.target.value })
                  }
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleEditModalClose}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleEditSubmit}
            disabled={!editFormData.frequency || !editFormData.endDate || !editFormData.departureTime}
          >
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RecurringBookingsModal;